import { authFetch } from '../../utils/authFetch';
import {
    getCache,
    getPending,
    getThumb,
    saveCache,
    saveThumb
} from './blobStore';

/**
 * Resolving a media id to its binary, offline-first:
 *   1. local display cache (instant, works offline),
 *   2. local pending upload (this device captured it, not yet uploaded),
 *   3. server fetch (another device's photo) — cached locally on success.
 *
 * Lives outside {@link useMediaBlobUrl} because re-rendering a sketch needs the
 * same chain with no React around it.
 */

export interface MediaBlobResult {
    /** The binary, or null when it isn't available on this device. */
    blob: Blob | null;
    /**
     * The binary isn't here YET — the origin device hasn't uploaded it, or we
     * can't reach the server. Distinct from a server that answered and said no:
     * this one is worth a placeholder and a later retry.
     */
    pending: boolean;
}

/** How far a download from the server has come, in bytes. */
export interface MediaBlobProgress {
    loaded: number;
    /** From Content-Length; null when the server did not say. */
    total: number | null;
}

export interface ResolveMediaOptions {
    /** Stop waiting — the caller is gone (a closed dialog, an unmounted card). */
    signal?: AbortSignal;
    onProgress?: (progress: MediaBlobProgress) => void;
    /**
     * A picture the device already holds, handed over at once while the server
     * is still asked whether there is a newer one — so a slow line shows the
     * last known picture instead of nothing. Previews only.
     */
    onCached?: (blob: Blob) => void;
}

/**
 * One server download per original, however many ask for it at once.
 *
 * An original is several megabytes, and over a field connection that is half a
 * minute. Opening the same photo twice — giving up and trying again — used to
 * start a second download next to the first, and both shared the line.
 */
interface Download {
    result: Promise<MediaBlobResult>;
    controller: AbortController;
    /** Callers still waiting; the download is cancelled once none are. */
    waiting: number;
    listeners: Set<(progress: MediaBlobProgress) => void>;
    progress: MediaBlobProgress | null;
}

const downloads = new Map<string, Download>();

/** Pending: whoever gave up waiting gets "not here yet", nothing worse. */
const GAVE_UP: MediaBlobResult = { blob: null, pending: true };

export async function resolveMediaBlob(
    id: string,
    options: ResolveMediaOptions = {}
): Promise<MediaBlobResult> {
    const cached = await getCache(id);
    if (cached) {
        return { blob: cached, pending: false };
    }

    const pendingEntry = await getPending(id);
    if (pendingEntry) {
        return { blob: pendingEntry.blob, pending: false };
    }

    return joinDownload(id, options);
}

function startDownload(id: string): Download {
    const controller = new AbortController();
    const download: Download = {
        result: Promise.resolve(GAVE_UP),
        controller,
        waiting: 0,
        listeners: new Set(),
        progress: null
    };
    download.result = downloadOriginal(id, controller.signal, (progress) => {
        download.progress = progress;
        download.listeners.forEach((listener) => listener(progress));
    }).finally(() => {
        if (downloads.get(id) === download) {
            downloads.delete(id);
        }
    });
    downloads.set(id, download);
    return download;
}

function joinDownload(
    id: string,
    { signal, onProgress }: ResolveMediaOptions
): Promise<MediaBlobResult> {
    if (signal?.aborted) {
        return Promise.resolve(GAVE_UP);
    }
    const download = downloads.get(id) ?? startDownload(id);
    download.waiting += 1;
    if (onProgress) {
        download.listeners.add(onProgress);
        if (download.progress) {
            onProgress(download.progress);
        }
    }

    return new Promise((resolve) => {
        let settled = false;
        function settle(result: MediaBlobResult) {
            if (settled) {
                return;
            }
            settled = true;
            signal?.removeEventListener('abort', giveUp);
            if (onProgress) {
                download.listeners.delete(onProgress);
            }
            download.waiting -= 1;
            resolve(result);
        }
        function giveUp() {
            settle(GAVE_UP);
            // The last one to leave turns off the light: nobody is going to
            // look at these bytes, and they are taking the line from whatever
            // is wanted instead.
            if (download.waiting === 0) {
                download.controller.abort();
                if (downloads.get(id) === download) {
                    downloads.delete(id);
                }
            }
        }
        signal?.addEventListener('abort', giveUp, { once: true });
        void download.result.then(settle);
    });
}

async function downloadOriginal(
    id: string,
    signal: AbortSignal,
    report: (progress: MediaBlobProgress) => void
): Promise<MediaBlobResult> {
    try {
        const response = await authFetch(`/api/media/${id}/blob`, { signal });
        if (!response.ok) {
            // 404: not uploaded yet. Anything else: unavailable, and not
            // something waiting will fix.
            return { blob: null, pending: response.status === 404 };
        }
        const blob = await readWithProgress(response, report);
        await saveCache(id, blob);
        return { blob, pending: false };
    } catch {
        // Offline, or cancelled because nobody waits any more.
        return GAVE_UP;
    }
}

/** The response body as a Blob, reporting every chunk as it arrives. */
async function readWithProgress(
    response: Response,
    report: (progress: MediaBlobProgress) => void
): Promise<Blob> {
    const type = response.headers.get('Content-Type') ?? '';
    const length = Number(response.headers.get('Content-Length'));
    const total = Number.isFinite(length) && length > 0 ? length : null;
    if (!response.body) {
        return response.blob();
    }
    const reader = response.body.getReader();
    const chunks: Uint8Array<ArrayBuffer>[] = [];
    let loaded = 0;
    report({ loaded, total });
    for (;;) {
        const { done, value } = await reader.read();
        if (done) {
            break;
        }
        chunks.push(value);
        loaded += value.byteLength;
        report({ loaded, total });
    }
    return new Blob(chunks, { type });
}

/** The binary alone, for callers with nothing to show while it's missing. */
export async function loadMediaBlob(id: string): Promise<Blob | null> {
    const { blob } = await resolveMediaBlob(id);
    return blob;
}

/**
 * A small preview for a gallery card or a picker tile, falling back to the
 * original wherever there is no preview to be had:
 *   1. a pending upload — the server has neither original nor preview yet,
 *   2. a cached preview goes to `onCached` straight away — never a cached
 *      original: a list would decode megabytes for a strip of 96 px,
 *   3. then the server's preview (made there on first request): a cached one is
 *      only asked about (If-None-Match), and a 304 costs no image at all,
 *   4. offline or failing: what the device holds.
 *
 * Asked about rather than trusted: an edited sketch keeps its id, and its doc
 * can arrive before its new image reaches the server — a preview filed under
 * the doc's version would then keep the old picture for good.
 *
 * The request is cancelled with `signal`: a card scrolled out of a list must
 * not go on taking the line from the sync and the uploads.
 */
export async function resolveMediaThumb(
    id: string,
    { signal, onCached }: ResolveMediaOptions = {}
): Promise<MediaBlobResult> {
    const pendingEntry = await getPending(id);
    if (pendingEntry) {
        return { blob: pendingEntry.blob, pending: false };
    }

    const cachedThumb = await getThumb(id);
    if (signal?.aborted) {
        return GAVE_UP;
    }
    if (cachedThumb) {
        onCached?.(cachedThumb.blob);
    }
    try {
        const response = await authFetch(`/api/media/${id}/thumb`, {
            headers: cachedThumb ? { 'If-None-Match': cachedThumb.etag } : {},
            signal
        });
        if (response.status === 304 && cachedThumb) {
            return { blob: cachedThumb.blob, pending: false };
        }
        if (response.ok) {
            const blob = await response.blob();
            // The server sends the original where it could not make a preview;
            // that belongs in the originals' cache, not among the previews.
            if (response.headers.get('X-Media-Variant') === 'original') {
                await saveCache(id, blob);
            } else {
                await saveThumb(id, blob, response.headers.get('ETag') ?? '');
            }
            return { blob, pending: false };
        }
        // 404 (not uploaded yet) and any failure: what this device holds,
        // else "not here yet".
    } catch {
        // Offline, or cancelled: fall through to what this device holds.
    }
    if (signal?.aborted) {
        return GAVE_UP;
    }

    const held = cachedThumb?.blob ?? (await getCache(id));
    return held ? { blob: held, pending: false } : GAVE_UP;
}
